import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAssetsData } from '../../moduleServices/assets_data_context';
import { computeAssetBalanceSummary } from './assets_balance_summary';
import {
  readCardAssetsDetailedState,
  persistCardAssetsDetailedState,
} from './assets_card_detailed_storage';
import { buildHierarchy, sortedLevel1Keys } from './assets_hierarchy';

const buildSnapshotMap = (items) => {
  const map = {};
  (items || []).forEach((item) => {
    map[item.asset] = {
      snapshot_value: parseFloat(item.snapshot_value) || 0,
      snapshot_quantity: parseFloat(item.snapshot_quantity) || 0,
    };
  });
  return map;
};

export function useAssetsSnapshotTable() {
  const {
    assets,
    categories,
    snapshots,
    snapshot_items: snapshotItems,
    loading,
    error,
    refreshFull,
  } = useAssetsData();

  const summary = useMemo(
    () => computeAssetBalanceSummary({ snapshots, snapshot_items: snapshotItems, assets }, {}),
    [snapshots, snapshotItems, assets]
  );
  const latestSnapshotId = summary.latest_snapshot?.id ?? null;

  const snapshotList = useMemo(() => {
    const list = [...snapshots];
    return list.sort((a, b) => (b.snapshot_date || '').localeCompare(a.snapshot_date || ''));
  }, [snapshots]);

  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);

  useEffect(() => {
    if (latestSnapshotId && selectedSnapshotId == null) {
      setSelectedSnapshotId(latestSnapshotId);
    }
  }, [latestSnapshotId, selectedSnapshotId]);

  const effectiveSnapshotId = selectedSnapshotId ?? latestSnapshotId;

  const snapshotMap = useMemo(() => {
    if (!effectiveSnapshotId) return {};
    const items = snapshotItems.filter((i) => i.snapshot === effectiveSnapshotId);
    return buildSnapshotMap(items);
  }, [effectiveSnapshotId, snapshotItems]);

  const snapshotDate = useMemo(() => {
    const snap = snapshotList.find((s) => s.id === effectiveSnapshotId);
    return snap?.snapshot_date ?? summary.latest_snapshot?.snapshot_date ?? null;
  }, [snapshotList, effectiveSnapshotId, summary.latest_snapshot]);

  const [hideInactive, setHideInactive] = useState(true);
  const [assetNameSearch, setAssetNameSearch] = useState('');
  const [assetTypeFilter, setAssetTypeFilter] = useState('');
  const [assetLocationFilter, setAssetLocationFilterState] = useState(null);
  const [cardAssetsState, setCardAssetsState] = useState(readCardAssetsDetailedState);

  const locationNames = useMemo(() => {
    const names = new Set();
    (assets || []).forEach((asset) => {
      const name = (asset.location_name || '').trim();
      if (name) names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [assets]);

  const setAssetLocationFilter = useCallback((next) => {
    if (Array.isArray(next) && locationNames.length > 0 && next.length === locationNames.length) {
      setAssetLocationFilterState(null);
      return;
    }
    setAssetLocationFilterState(Array.isArray(next) ? next : null);
  }, [locationNames]);

  const onSelectSnapshot = useCallback((snapshotId) => {
    if (!snapshotId) {
      setSelectedSnapshotId(null);
      return;
    }
    setSelectedSnapshotId(snapshotId);
  }, []);

  const onTableStartLevelChange = useCallback((level) => {
    setCardAssetsState((prev) => {
      const next = { ...prev, tableStartLevel: level };
      persistCardAssetsDetailedState(next);
      return next;
    });
  }, []);

  const onToggleShowOnlyWithValue = useCallback((value) => {
    setCardAssetsState((prev) => {
      const next = { ...prev, showOnlyInSnapshot: value };
      persistCardAssetsDetailedState(next);
      return next;
    });
  }, []);

  const showOnlyInSnapshot = cardAssetsState.showOnlyInSnapshot;
  const filteredByActive = hideInactive ? assets.filter((a) => a.is_active) : assets;
  const withSnapshot = showOnlyInSnapshot
    ? filteredByActive.filter((a) => (snapshotMap[a.id]?.snapshot_value ?? 0) !== 0)
    : filteredByActive;
  const searchLower = assetNameSearch.trim().toLowerCase();
  const byName = searchLower
    ? withSnapshot.filter((a) => (a.asset_name || '').toLowerCase().includes(searchLower))
    : withSnapshot;
  const byType = assetTypeFilter
    ? byName.filter((a) => (a.asset_type || 'tradeable') === assetTypeFilter)
    : byName;
  const displayedAssets = assetLocationFilter == null
    ? byType
    : byType.filter((a) => assetLocationFilter.includes((a.location_name || '').trim()));
  const hierarchy = buildHierarchy(displayedAssets, categories);
  const sortedLevel1 = sortedLevel1Keys(hierarchy);

  return {
    loading,
    error,
    refreshFull,
    displayedAssets,
    hierarchy,
    sortedLevel1,
    snapshotMap,
    snapshotDate,
    snapshotList,
    selectedSnapshotId: effectiveSnapshotId,
    onSelectSnapshot,
    tableStartLevel: cardAssetsState.tableStartLevel,
    onTableStartLevelChange,
    hideInactive,
    setHideInactive,
    assetNameSearch,
    setAssetNameSearch,
    assetTypeFilter,
    setAssetTypeFilter,
    assetLocationFilter,
    setAssetLocationFilter,
    locationNames,
    showOnlyInSnapshot,
    onToggleShowOnlyWithValue,
  };
}
